if (!customElements.get('collection-infinite-scroll')) {
  class CollectionInfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
    }

    connectedCallback() {
      console.log('[InfiniteScroll] Initializing...');
      this.loader = this.querySelector('.collection-infinite-scroll__loader');
      this.productGrid = document.getElementById('product-grid');
      this.sectionId = this.productGrid?.dataset?.id || this.dataset.sectionId || '';
      this.nextUrl = this.dataset.nextUrl || this.querySelector('[data-infinite-scroll-next]')?.getAttribute('href') || '';
      this.sentinel = this.querySelector('.collection-infinite-scroll__sentinel') || this;
      
      // Force physical dimensions so IntersectionObserver doesn't ignore it
      if (this.sentinel !== this) {
        this.sentinel.style.height = '1px';
        this.sentinel.style.width = '100%';
        this.sentinel.style.display = 'block';
      }

      console.log('[InfiniteScroll] Initial nextUrl:', this.nextUrl);
      console.log('[InfiniteScroll] Section ID:', this.sectionId);

      if (!this.nextUrl || !this.productGrid) {
        console.log('[InfiniteScroll] Aborting init: missing nextUrl or productGrid');
        return;
      }

      this.checkIntersection = () => {
        if (this.loading || !this.nextUrl) return;
        const rect = this.sentinel.getBoundingClientRect();
        const inRange = rect.top < window.innerHeight + 400 && rect.bottom > -400;
        if (inRange) {
          console.log('[InfiniteScroll] Fallback check triggered loadNextPage');
          this.loadNextPage();
        }
      };

      this.observer = new IntersectionObserver((entries) => {
        console.log('[InfiniteScroll] Sentinel intersected:', entries[0].isIntersecting);
        if (entries[0].isIntersecting && !this.loading && this.nextUrl) {
          console.log('[InfiniteScroll] Triggering loadNextPage() from Observer');
          this.loadNextPage();
        }
      }, { rootMargin: '400px' });

      this.observer.observe(this.sentinel);
      console.log('[InfiniteScroll] Observer attached to sentinel');
      
      // Fallback scroll listener just in case Observer fails on this layout
      window.addEventListener('scroll', this.checkIntersection, { passive: true });
      
      // Check immediately on load in case it's already in view
      setTimeout(this.checkIntersection, 100);
    }

    disconnectedCallback() {
      console.log('[InfiniteScroll] Disconnected');
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('scroll', this.checkIntersection);
    }

    showLoader() {
      this.classList.add('is-loading');
      this.loader?.classList.remove('hidden');
    }

    hideLoader() {
      this.classList.remove('is-loading');
      this.loader?.classList.add('hidden');
    }

    buildFetchUrl() {
      const next = new URL(this.nextUrl, window.location.href);
      const fetchUrl = new URL(next.pathname, window.location.origin);
      
      next.searchParams.forEach((value, key) => {
        if (key === 'section_id' || key === 'sections') return;
        fetchUrl.searchParams.set(key, value);
      });

      const current = new URL(window.location.href);
      current.searchParams.forEach((value, key) => {
        if (key === 'page' || key === 'section_id' || key === 'sections') return;
        if (!fetchUrl.searchParams.has(key)) {
          fetchUrl.searchParams.set(key, value);
        }
      });

      if (this.sectionId) {
        fetchUrl.searchParams.set('section_id', this.sectionId);
      }

      console.log('[InfiniteScroll] Built fetch URL:', fetchUrl.toString());
      return fetchUrl.toString();
    }

    getGridItems(grid) {
      return Array.from(grid.children).filter((item) => item.classList.contains('js-grid__item'));
    }

    revealLoadedItems(items) {
      items.forEach((item, index) => {
        if (!item.classList.contains('scroll-trigger')) return;
        item.classList.remove('scroll-trigger--cancel', 'scroll-trigger--offscreen', 'animate--slide-in');
        item.style.setProperty('--animation-order', String(index));
        void item.offsetWidth;
        item.classList.add('animate--slide-in');
      });
    }

    async loadNextPage() {
      console.log('[InfiniteScroll] Starting loadNextPage...');
      this.loading = true;
      this.showLoader();

      try {
        const urlToFetch = this.buildFetchUrl();
        console.log('[InfiniteScroll] Fetching:', urlToFetch);
        const response = await fetch(urlToFetch);
        
        if (!response.ok) {
          console.error('[InfiniteScroll] Fetch failed with status:', response.status);
          throw new Error('Failed to load collection page');
        }

        const html = await response.text();
        console.log('[InfiniteScroll] Fetched HTML length:', html.length);
        
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fetchedGrid = doc.getElementById('product-grid');
        const fetchedScroll = doc.querySelector('collection-infinite-scroll');

        if (fetchedGrid) {
          const newItems = this.getGridItems(fetchedGrid);
          console.log('[InfiniteScroll] Found items to append:', newItems.length);
          
          newItems.forEach((item) => {
            this.productGrid.appendChild(item);
          });
          
          this.revealLoadedItems(newItems);
          console.log('[InfiniteScroll] Successfully appended items');
        } else {
          console.warn('[InfiniteScroll] Could not find #product-grid in response');
        }

        if (fetchedScroll) {
          const nextLink = fetchedScroll.querySelector('[data-infinite-scroll-next]');
          this.nextUrl = fetchedScroll.dataset.nextUrl || nextLink?.getAttribute('href') || '';
          console.log('[InfiniteScroll] Updated nextUrl to:', this.nextUrl);
          
          const localNextLink = this.querySelector('[data-infinite-scroll-next]');
          if (this.nextUrl && localNextLink) {
            localNextLink.setAttribute('href', this.nextUrl);
          }
        } else {
          console.log('[InfiniteScroll] No next page found (fetchedScroll missing)');
          this.nextUrl = '';
        }

        if (typeof window.adjustBannerHeights === 'function') {
          window.adjustBannerHeights();
        }

      } catch (error) {
        console.error('[InfiniteScroll] Error:', error);
      } finally {
        console.log('[InfiniteScroll] loadNextPage finished');
        this.loading = false;
        this.hideLoader();
        
        if (!this.nextUrl && this.observer) {
          console.log('[InfiniteScroll] Disconnecting observer as there are no more pages');
          this.observer.disconnect();
        }
      }
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
}
